import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, workerNavItems, teamLeaderNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { 
  Clock, 
  CheckCircle2, 
  XCircle,
  AlertCircle,
  MapPin,
  Calendar,
  Users,
  DollarSign,
  Building2,
  Briefcase,
  Eye
} from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkerApplications() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Use correct nav items based on user role
  const navItems = user?.role === "team_leader" ? teamLeaderNavItems : workerNavItems;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/my-applications`);
        setApplications(response.data || []);
      } catch (error) {
        console.error("Error fetching applications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getApplicationStatus = (app) => {
    if (app.status === "rejected") {
      return { label: t('common.rejected'), color: "red", icon: XCircle, description: t('applications.rejectedApplications') };
    }
    if (app.status === "not_selected") {
      return { label: t('common.rejected'), color: "gray", icon: XCircle, description: t('applications.rejectedApplications') };
    }
    if (app.status === "accepted") {
      return { label: t('common.approved'), color: "green", icon: CheckCircle2, description: t('applications.approvedApplications') };
    }
    if (app.status === "team_signed") {
      return { label: t('contracts.signed'), color: "blue", icon: CheckCircle2, description: t('common.pending') };
    }
    if (app.status === "pending") {
      return { label: t('common.pending'), color: "yellow", icon: Clock, description: t('applications.pendingApplications') };
    }
    return { label: app.status || t('common.pending'), color: "yellow", icon: Clock, description: t('applications.applicationSubmitted') };
  };

  const getStatusBadge = (status) => {
    const colors = {
      red: "bg-red-500/20 text-red-400 border-red-500/30",
      green: "bg-green-500/20 text-green-400 border-green-500/30",
      yellow: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      blue: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      orange: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      gray: "bg-gray-500/20 text-gray-400 border-gray-500/30"
    };
    
    const Icon = status.icon;
    
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${colors[status.color]}`}>
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{status.label}</span>
      </div>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nb-NO', { 
      style: 'currency', 
      currency: 'NOK',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout navItems={navItems} title="My Applications">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={navItems} title={t('workerApplications.title')}>
      <div className="space-y-6" data-testid="worker-applications">
        {/* Header */}
        <div>
          <h2 className="text-xl font-['Unbounded'] font-semibold text-white mb-2">
            {t('workerApplications.myProjectApplications')}
          </h2>
          <p className="text-[#94A3B8]">
            {t('workerApplications.viewStatusDescription')}
          </p>
        </div>

        {/* Status Legend */}
        <div className="flex flex-wrap gap-4 p-4 bg-white/5 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span>{t('workerApplications.pendingReview')}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>{t('workerApplications.accepted')}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span>{t('workerApplications.contractSigned')}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
            <div className="w-3 h-3 rounded-full bg-gray-500"></div>
            <span>{t('workerApplications.notSelected')}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span>{t('workerApplications.rejected')}</span>
          </div>
        </div>

        {/* Applications List */}
        {applications.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">{t('workerApplications.noApplicationsYet')}</h3>
              <p className="text-[#94A3B8] mb-4">
                {t('workerApplications.noApplicationsDescription')}
              </p>
              <Link to="/projects">
                <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                  {t('workerApplications.browseProjects')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => {
              const status = getApplicationStatus(app);
              const project = app.project || {};
              const projectTitle = project.title || app.project_title || "Project";
              const locationCity = project.location_city || app.location_city;
              const projectType = project.project_type || app.project_type;
              const crewSize = project.required_crew_size || app.required_crew_size;
              const budget = project.net_amount_for_workers || project.budget_max || app.budget;
              
              return (
                <Card key={app.application_id} className="bg-[#0A0A0A] border-white/10 hover:border-white/20 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      {/* Project Info */}
                      <div className="flex-1 space-y-4">
                        {/* Title and Status */}
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <h3 className="text-lg font-['Unbounded'] font-semibold text-white">
                            {projectTitle}
                          </h3>
                          {getStatusBadge(status)}
                        </div>
                        
                        {/* Project Details Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {locationCity && (
                            <div className="flex items-center gap-2 text-sm">
                              <MapPin className="w-4 h-4 text-[#23F7DD]" />
                              <span className="text-[#94A3B8]">{locationCity}</span>
                            </div>
                          )}
                          {projectType && (
                            <div className="flex items-center gap-2 text-sm">
                              <Briefcase className="w-4 h-4 text-[#23F7DD]" />
                              <span className="text-[#94A3B8] capitalize">{projectType}</span>
                            </div>
                          )}
                          {crewSize && (
                            <div className="flex items-center gap-2 text-sm">
                              <Users className="w-4 h-4 text-[#23F7DD]" />
                              <span className="text-[#94A3B8]">{crewSize} {t('nav.workers').toLowerCase()}</span>
                            </div>
                          )}
                          {budget > 0 && (
                            <div className="flex items-center gap-2 text-sm">
                              <DollarSign className="w-4 h-4 text-[#23F7DD]" />
                              <span className="text-[#94A3B8]">{formatCurrency(budget)}</span>
                            </div>
                          )}
                        </div>

                        {/* Application Info */}
                        <div className="flex flex-wrap gap-4 pt-3 border-t border-white/10">
                          <div className="flex items-center gap-2 text-sm text-[#64748B]">
                            <Calendar className="w-4 h-4" />
                            {t('workerApplications.applied')}: {new Date(app.created_at).toLocaleDateString('nb-NO')}
                          </div>
                          {app.team_members && app.team_members.length > 0 && (
                            <div className="flex items-center gap-2 text-sm text-[#64748B]">
                              <Users className="w-4 h-4" />
                              {t('workerApplications.team')}: {app.team_members.length} {t('workerApplications.members')}
                            </div>
                          )}
                        </div>

                        {/* Status Description */}
                        <p className="text-sm text-[#64748B] italic">
                          {status.description}
                        </p>

                        {/* Show hint for accepted applications */}
                        {status.color === "green" && (
                          <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                            <p className="text-sm text-green-400">
                              ✓ {t('workerApplications.acceptedHint')} <Link to="/team-leader/agreements" className="underline font-medium">{t('nav.agreements')}</Link> {t('workerApplications.toSignContract')}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* View Project Button */}
                      <div className="flex-shrink-0">
                        {app.project_id && (
                          <Link to={`/my-projects/${app.project_id}`}>
                            <Button 
                              variant="outline"
                              className="border-[#23F7DD]/30 text-[#23F7DD] hover:bg-[#23F7DD]/10"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              {t('workerApplications.viewProject')}
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
